/**
 * Pure helpers for Slice 18f — batch "Suggest template" for un-templated rows.
 *
 * The actual model calls reuse the per-post `ads-ingest-templateize` endpoint;
 * these helpers only handle shaping the input/output around the existing
 * server contract so the batch orchestration stays testable without OpenAI.
 */

import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClientTypes";

/** Per-row outcome in a batch run. `pending` rows have not been processed yet. */
export type AdsBatchTemplateizeResult =
  | { post_id: string; status: "pending" }
  | { post_id: string; status: "running" }
  | {
      post_id: string;
      status: "proposed";
      template_id: string | null;
      template_name: string | null;
      slot_fills: Record<string, string>;
      notes: string;
      /** Set true when the user toggles this row's "accept" in the review step. */
      accepted: boolean;
    }
  | {
      post_id: string;
      status: "applied";
      template_id: string;
      template_name: string | null;
    }
  | { post_id: string; status: "skipped"; reason: string }
  | { post_id: string; status: "error"; error: string };

/** Inputs the existing per-post `/api/ads-assistant/ingest-templateize` accepts. */
export type AdsBatchTemplateizeRequestForPost = {
  ad_copy: string;
  creative_description?: string;
};

const AD_COPY_TRIM_MAX = 12_000;
const CREATIVE_DESCRIPTION_TRIM_MAX = 4_000;

/**
 * Pull a usable `ad_copy` (+ optional `creative_description`) out of a row.
 * Returns `null` when the row has neither text nor a creative description —
 * those rows are skipped before any model call.
 */
export function extractAdCopyForBatch(
  post: Pick<AdsAssistantSocialPostRow, "text_content" | "raw_payload">
): AdsBatchTemplateizeRequestForPost | null {
  const text = typeof post.text_content === "string" ? post.text_content.trim() : "";
  const rawCreative =
    post.raw_payload && typeof post.raw_payload === "object"
      ? (post.raw_payload as Record<string, unknown>).creative_description
      : null;
  const creative = typeof rawCreative === "string" ? rawCreative.trim() : "";

  if (!text && !creative) return null;

  const out: AdsBatchTemplateizeRequestForPost = {
    ad_copy: (text || creative).slice(0, AD_COPY_TRIM_MAX),
  };
  if (creative) {
    out.creative_description = creative.slice(0, CREATIVE_DESCRIPTION_TRIM_MAX);
  }
  return out;
}

/** Build the PATCH body to apply an accepted proposal to a social post row. */
export function buildApplyPatch(result: AdsBatchTemplateizeResult): {
  template_id: string;
  slot_fill_json: Record<string, unknown> | null;
} | null {
  if (result.status !== "proposed" || !result.accepted) return null;
  if (!result.template_id) return null;
  const hasSlots = Object.keys(result.slot_fills).length > 0;
  return {
    template_id: result.template_id,
    slot_fill_json: hasSlots ? result.slot_fills : null,
  };
}

/** Counts used by the dialog header to summarize progress and outcomes. */
export type AdsBatchTemplateizeSummary = {
  total: number;
  pending: number;
  running: number;
  proposed: number;
  proposed_with_template: number;
  proposed_no_fit: number;
  accepted: number;
  applied: number;
  skipped: number;
  errors: number;
};

export function summarizeBatchResults(
  results: ReadonlyArray<AdsBatchTemplateizeResult>
): AdsBatchTemplateizeSummary {
  const out: AdsBatchTemplateizeSummary = {
    total: results.length,
    pending: 0,
    running: 0,
    proposed: 0,
    proposed_with_template: 0,
    proposed_no_fit: 0,
    accepted: 0,
    applied: 0,
    skipped: 0,
    errors: 0,
  };
  for (const r of results) {
    switch (r.status) {
      case "pending":
        out.pending += 1;
        break;
      case "running":
        out.running += 1;
        break;
      case "proposed":
        out.proposed += 1;
        if (r.template_id) out.proposed_with_template += 1;
        else out.proposed_no_fit += 1;
        if (r.accepted) out.accepted += 1;
        break;
      case "applied":
        out.applied += 1;
        break;
      case "skipped":
        out.skipped += 1;
        break;
      case "error":
        out.errors += 1;
        break;
    }
  }
  return out;
}

/** Cap on how many posts the user can queue in one batch run. */
export const ADS_BATCH_TEMPLATEIZE_SELECTION_MAX = 20;

/** Cap on how many un-templated posts we ever fetch into the dialog. */
export const ADS_BATCH_TEMPLATEIZE_FETCH_MAX = 100;

/** Sleep between sequential model calls to avoid spiking OpenAI rate limits. */
export const ADS_BATCH_TEMPLATEIZE_PACING_MS = 250;
